/* global google, AppSettings */
/**
 * This is handler for Google maps search API
 *
 * @author Tamara Bazko
 **/
import $ from 'jquery';
import createLogger from 'components/logger/Logger';
import AnalyticsEventTypes from 'components/analytics/AnalyticsEventTypes';
import EventTypes from 'components/EventTypes';
import getCountryCode from '../utils/getCountryCode';
import getMapAdminLevelConfig from '../utils/getMapAdminLevelConfig';

const Logger = createLogger('googleMapSearch');

let fromEnter = false;

const GoogleMapSearch = {
  /**
   **** PUBLIC PROPERTIES ****
   */
  $inputElement: undefined,

  /**
   **** PRIVATE PROPERTIES ****
   */
  _$resultsContainer: undefined,
  _resultsContainerSelector: '.pac-container',
  _searchBox: undefined,
  _place: undefined,
  _keyboardNavigationHandler: undefined,

  /******************* PUBLIC METHODS *******************
   *
   * Bind events
   */
  bindEvents() {
    google.maps.event.addListener(
      this._searchBox,
      'places_changed',
      this._triggerEvent.bind(this, 'places_changed')
    );

    this._bindKeyboardNavigation();
    this._monitorDropdownVisibility();
  },

  /**
   * Create google searchbox
   * @param  {Object}   settings
   */
  createSearchBox(settings) {
    this.$inputElement = $(settings.inputElement);
    this._searchBox = new google.maps.places.SearchBox(settings.inputElement);
    this._setupAccessibility();
    return this._searchBox;
  },

  _setupAccessibility() {
    this._resetAccessibilityState();
  },

  _resetAccessibilityState() {
    const inputElement = this.$inputElement && this.$inputElement.get(0);
    if (!inputElement) {
      return;
    }

    inputElement.removeAttribute('aria-activedescendant');
    inputElement.removeAttribute('aria-owns');
    inputElement.removeAttribute('aria-controls');
    inputElement.removeAttribute('aria-autocomplete');

    inputElement.setAttribute('role', 'combobox');
    inputElement.setAttribute('aria-haspopup', 'listbox');
    inputElement.setAttribute('aria-expanded', 'false');
  },

  _clearActiveDescendant() {
    const inputElement = this.$inputElement && this.$inputElement.get(0);
    if (!inputElement) {
      return;
    }
    inputElement.removeAttribute('aria-activedescendant');
  },

  _monitorDropdownVisibility() {
    const observer = new MutationObserver(() => {
      const pacContainer = document.querySelector('.pac-container');
      const inputElement = this.$inputElement && this.$inputElement.get(0);

      if (!inputElement) {
        return;
      }

      if (pacContainer) {
        const isVisible =
          pacContainer.offsetParent !== null &&
          window.getComputedStyle(pacContainer).display !== 'none';

        if (isVisible) {
          if (inputElement.getAttribute('aria-expanded') !== 'true') {
            inputElement.setAttribute('aria-expanded', 'true');
          }
        } else {
          if (inputElement.getAttribute('aria-expanded') !== 'false') {
            inputElement.setAttribute('aria-expanded', 'false');
          }
          this._clearActiveDescendant();
        }
      } else {
        if (inputElement.getAttribute('aria-expanded') !== 'false') {
          inputElement.setAttribute('aria-expanded', 'false');
        }
        this._clearActiveDescendant();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  },

  _bindKeyboardNavigation() {
    const inputElement = this.$inputElement && this.$inputElement.get(0);

    if (!inputElement) {
      return;
    }

    if (this._keyboardNavigationHandler) {
      inputElement.removeEventListener('keydown', this._keyboardNavigationHandler);
    }

    this._keyboardNavigationHandler = event => {
      const isArrowDown = event.key === 'ArrowDown';
      const isArrowUp = event.key === 'ArrowUp';
      const isEnter = event.key === 'Enter';
      const isTab = event.key === 'Tab';
      const isEscape = event.key === 'Escape' || event.key === 'Esc';

      if (isArrowDown || isArrowUp) {
        requestAnimationFrame(() => {
          this._updateAriaForSelectedSuggestion();
        });
      } else if (isEnter || isTab || isEscape) {
        this._clearActiveDescendant();
        if (isEscape) {
          inputElement.setAttribute('aria-expanded', 'false');
        }
      } else {
        this._clearActiveDescendant();
      }
    };

    inputElement.addEventListener('keydown', this._keyboardNavigationHandler);

    inputElement.addEventListener('input', () => {
      if (!inputElement.value) {
        this._resetAccessibilityState();
      } else {
        this._clearActiveDescendant();
      }
    });

    inputElement.addEventListener('blur', () => {
      this._resetAccessibilityState();
    });
  },

  _updateAriaForSelectedSuggestion() {
    const pacContainer = document.querySelector('.pac-container');
    const pacItemSelected = document.querySelector('.pac-item-selected');

    if (!pacContainer || !pacItemSelected) {
      return;
    }

    if (!pacContainer.getAttribute('role')) {
      pacContainer.setAttribute('role', 'listbox');
      pacContainer.setAttribute('id', 'google-maps-suggestions-listbox');
    }

    if (!pacContainer.dataset.gstarA11yHooked) {
      pacContainer.dataset.gstarA11yHooked = 'true';

      pacContainer.addEventListener('focusin', () => {
        const inputElement = this.$inputElement && this.$inputElement.get(0);
        if (inputElement) {
          inputElement.removeAttribute('aria-activedescendant');
        }
      });
    }

    const allItems = pacContainer.querySelectorAll('.pac-item');
    allItems.forEach((item, index) => {
      if (!item.getAttribute('id')) {
        item.setAttribute('id', `google-maps-suggestion-${index}`);
      }
      if (!item.getAttribute('role')) {
        item.setAttribute('role', 'option');
      }
      item.removeAttribute('aria-selected');
    });

    const optionId = pacItemSelected.getAttribute('id');

    if (!optionId) {
      return;
    }

    pacItemSelected.setAttribute('aria-selected', 'true');

    const inputElement = this.$inputElement && this.$inputElement.get(0);
    if (!inputElement) {
      return;
    }

    if (document.activeElement === inputElement) {
      inputElement.setAttribute('aria-activedescendant', optionId);
    } else {
      inputElement.removeAttribute('aria-activedescendant');
    }

    if (!inputElement.getAttribute('aria-controls')) {
      inputElement.setAttribute('aria-controls', 'google-maps-suggestions-listbox');
    }
  },

  _getFullTextFromPacItem(pacItem) {
    if (!pacItem) {
      return '';
    }

    let fullText = pacItem.textContent || pacItem.innerText || '';
    fullText = fullText.replace(/\s+/g, ' ').trim();

    return fullText;
  },

  /**
   * Get place geometry
   * @return   {Object}   geometry   Place data
   */
  getPlaceGeometry() {
    const places = this._searchBox.getPlaces();
    const place = places[0];

    if (place && !!place.address_components) {
      this._place = place;

      if (!fromEnter) {
        const searchInput = document.querySelector('.js-storeFinder-search-input');
        this.pushData(searchInput);
      } else {
        fromEnter = false;
      }

      return this._place.geometry;
    } else if (place) {
      if (this.selectFirstResult()) {
        Logger.debug('Place does not have address components, selecting first result.');
        return;
      } else {
        Logger.warn(
          'Place does not have address components, it is possible stores will not be retrieved.'
        );
        this._place = place;
        return this._place.geometry;
      }
    }
    return false;
  },

  /**
   * Push data
   * @param {undefined} input Search input element
   */
  pushData(input) {
    const isStockLocator = document.querySelector('.js-stockLocator');
    if (!input || isStockLocator) {
      return;
    }

    const filterList = document.querySelector('.js-storeFinder-filter-list');
    const filterItems =
      filterList && Array.from(filterList.querySelectorAll('.js-storeFinder-filter-item'));
    const activeFilterItems =
      filterItems &&
      filterItems.length &&
      filterItems.filter(filterItem => filterItem.classList.contains('is-active'));
    const activeFilters =
      activeFilterItems &&
      activeFilterItems.map(activeFilterItem => activeFilterItem.dataset.filterTextEn).join(' | ');

    document.jq.trigger(AnalyticsEventTypes.STORE_FINDER_SEARCH, {
      label: activeFilters && activeFilterItems.length ? activeFilters.toLowerCase() : undefined,
      searchTerm: input.value.toLowerCase(),
    });
  },

  /**
   * Hide or show suggested search (container with results)
   * @param   {Boolean}    isHidden
   */
  hideSuggestedSearch(isHidden) {
    if (!this._$resultsContainer || this._$resultsContainer.length === 0) {
      this._$resultsContainer = $(this._resultsContainerSelector);
    }

    this._$resultsContainer.toggleClass('is-hidden', isHidden);
  },

  /**
   * Create custom keyboard event
   * @param {eventType} type eventType of keyboard event
   * @param {keyCode} keyArg keyCode of keyboard event
   */
  createKeyboardEvent(type, keyArg) {
    const event = document.createEvent('KeyboardEvent');
    event.initKeyboardEvent(type, false, true, window.top, keyArg, 0, '', false, 'en-US');
    return event;
  },

  /**
   * Select first result in list
   */
  selectFirstResult() {
    fromEnter = true;

    if (document.getElementsByClassName('pac-item').length > 0) {
      const input = this.$inputElement.get(0);
      input.focus();

      let kEventDown;
      let kEventEnter;
      try {
        kEventDown = new KeyboardEvent('keydown', { keyCode: 40 });
        kEventEnter = new KeyboardEvent('keydown', { keyCode: 13 });
      } catch (e) {
        kEventDown = this.createKeyboardEvent('keydown', 'Down');
        kEventEnter = this.createKeyboardEvent('keydown', 'Enter');
      }

      this.pushData(input);

      google.maps.event.trigger(input, 'keydown', kEventDown);
      google.maps.event.trigger(input, 'keydown', kEventEnter);

      return true;
    }
    return false;
  },

  /**
   * Trigger search by triggering enter key down (for search button, as there is no API search button)
   */
  triggerEnterKey($element) {
    this.$inputElement.focus();
    google.maps.event.trigger($element[0], 'keydown', { keyCode: 13 });
  },

  /**
   * Trigger event with data of the place searched through
   * search box
   */
  placeSelected() {
    let address = '';
    const searchParameters = getMapAdminLevelConfig(this._place);
    if (this._place.address_components) {
      address = [
        (this._place.address_components[0] && this._place.address_components[0].short_name) || '',
        (this._place.address_components[1] && this._place.address_components[1].short_name) || '',
        (this._place.address_components[2] && this._place.address_components[2].short_name) || '',
      ].join(' ');
    }

    if (searchParameters) {
      const searchText = this.$inputElement.val();
      if (
        !this._place.address_components &&
        searchParameters.country === AppSettings.country &&
        searchText.toLowerCase() !== AppSettings.countryName.toLowerCase()
      ) {
        searchParameters.country(getCountryCode(searchText, searchParameters.country));
      }

      if (searchParameters && searchParameters.country) {
        const country = $.trim(address).toLowerCase();
        if (country.length === 2) {
          this.$inputElement.trigger(EventTypes.STOREFINDER_GET_USER_LOCATION, {
            country,
            place: this._place,
          });
          return;
        }
      }

      if (searchParameters) {
        Logger.debug('Apply search parameters:', searchParameters);
      }
    }

    this.$inputElement.trigger(EventTypes.STOREFINDER_GET_USER_LOCATION, {
      coordinates: {
        longitude: this._place.geometry.location.lng(),
        latitude: this._place.geometry.location.lat(),
      },
      address,
      place: this._place,
      searchParameters,
    });
  },

  /******************* PRIVATE METHODS *******************
   *
   * Trigger event
   * @param   {String}   eventType   event name
   */
  _triggerEvent(eventType) {
    if (eventType === 'places_changed') {
      this._resetAccessibilityState();
      this.$inputElement.trigger(EventTypes.STOREFINDER_SEARCH_PLACES_CHANGED);
    }
  },
};

export default GoogleMapSearch;
